import { z } from 'zod';
import {
  accountMembershipSchema,
  assistantConfigurationSchema,
  storedConfigurationDraftSchema,
  businessActionSchema,
  calendarBookingReceiptSchema,
  moneyMinorStringSchema,
  sourceFactSchema,
  type BotScope,
} from '@cryptology-assist/domain';

const scopeSchema = z.object({
  company_id: z.string(),
  bot_id: z.string(),
  name: z.string(),
  role: z.enum(['owner', 'admin', 'operator', 'viewer']),
  status: z.string(),
});
export const sessionSchema = z.object({
  canCreateInstallation: z.boolean().default(false),
  accountId: z.string(),
  name: z.string(),
  csrf: z.string(),
  memberships: z.array(scopeSchema),
  accounts: z.array(accountMembershipSchema),
});
export type Session = z.infer<typeof sessionSchema>;
export const providersSchema = z.object({
  providers: z.array(z.enum(['google', 'microsoft'])),
});
export const snapshotSchema = z.object({
  draft: storedConfigurationDraftSchema.nullable().optional(),
  providers: providersSchema.shape.providers,
  release: z
    .object({
      id: z.string(),
      configuration_json: assistantConfigurationSchema,
      created_at: z.string(),
    })
    .nullable(),
  connections: z.array(
    z.object({
      id: z.string(),
      channel: z.enum(['chat', 'email']),
      provider: z.string(),
      address: z.string().nullable(),
      status: z.string(),
      error: z.string().nullable(),
      lastSyncedAt: z.string().nullable(),
    })
  ),
  mailFailures: z.array(
    z.object({
      id: z.string(),
      connection_id: z.string(),
      provider_message_id: z.string(),
      error_code: z.string(),
      attempts: z.number().int(),
      last_seen_at: z.string(),
    })
  ),
  businessConnections: z.array(
    z.object({
      id: z.string(),
      provider: z.string(),
      endpoint: z.string(),
      capabilities: z.array(z.string()),
      status: z.string(),
      error_code: z.string().nullable(),
    })
  ),
});
export type Snapshot = z.infer<typeof snapshotSchema>;
export const conversationSchema = z.object({
  id: z.string(),
  channel: z.enum(['web', 'email']),
  automation_mode: z.enum(['automatic', 'human', 'stopped']),
  processing_error: z.string().nullable(),
  assigned_user_id: z.string().nullable(),
  review_due_at: z.string().nullable(),
  verified_subject: z.string().nullable(),
  subject_verified_until: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export const conversationsSchema = z.object({
  conversations: z.array(
    conversationSchema.extend({
      subject: z.string().nullable(),
      customerAddress: z.string().nullable(),
      needs_attention: z.boolean(),
    })
  ),
});
export const detailSchema = z.object({
  conversation: conversationSchema,
  decision: z
    .object({
      sourceEventId: z.string(),
      model: z.string(),
      createdAt: z.string(),
      decision: z.object({
        intent: z.string(),
        paymentClaim: z.boolean(),
        dispute: z.boolean(),
        stop: z.boolean(),
        needsHuman: z.boolean(),
        evidence: z.array(z.string()),
      }),
    })
    .nullable(),
  handoffDecision: z
    .object({
      sourceEventId: z.string(),
      model: z.string(),
      createdAt: z.string(),
      decision: z.object({
        intent: z.string(),
        paymentClaim: z.boolean(),
        dispute: z.boolean(),
        stop: z.boolean(),
        needsHuman: z.boolean(),
        evidence: z.array(z.string()),
      }),
    })
    .nullable(),
  failedEvents: z.array(
    z.object({
      text: z.string(),
      classified: z.boolean(),
      id: z.string(),
      error_code: z.string().nullable(),
      created_at: z.string(),
    })
  ),
  messages: z.array(
    z.object({
      id: z.string(),
      role: z.string(),
      text: z.string(),
      occurredAt: z.string(),
      deliveryStatus: z.string().nullable(),
    })
  ),
  holds: z.array(
    z.object({
      id: z.string(),
      reason: z.string(),
      created_at: z.string(),
      source_event_id: z.string().nullable(),
      sourceText: z.string().nullable(),
      case_action_id: z.string().nullable(),
    })
  ),
  actions: z.array(
    z.object({
      id: z.string(),
      status: z.string(),
      request: businessActionSchema,
      errorCode: z.string().nullable(),
      recoveryKind: z.enum(['replay', 'reconcile']),
      attempts: z.number().int().nonnegative(),
      calendarBooking: calendarBookingReceiptSchema.nullable(),
      result: z
        .object({
          reference: z.string(),
          status: z.string(),
          facts: z.array(sourceFactSchema),
        })
        .nullable(),
    })
  ),
  outbox: z.array(
    z.object({
      id: z.string(),
      status: z.string(),
      error_code: z.string().nullable(),
      content: z.object({ text: z.string() }),
    })
  ),
  attachments: z.array(
    z.object({
      id: z.string(),
      filename: z.string(),
      content_type: z.string(),
      review_status: z.string(),
      size_bytes: z.string(),
    })
  ),
});
export type ConversationDetail = z.infer<typeof detailSchema>;
export const knowledgeSchema = z.object({
  sources: z.array(
    z.object({
      id: z.string(),
      source_type: z.string(),
      canonical_uri: z.string(),
      status: z.string(),
      revision: z.string(),
      captured_at: z.string(),
    })
  ),
});
export const calendarsSchema = z.object({
  calendars: z.array(
    z.object({ id: z.string(), name: z.string(), writable: z.boolean() })
  ),
});
export const usageSchema = z.object({
  subscription: z.object({
    plan_code: z.string(),
    monthly_price_usd_minor: moneyMinorStringSchema,
    included_conversations: z.number(),
    current_period_starts_at: z.string(),
    current_period_ends_at: z.string(),
    status: z.string(),
  }),
  conversations: z.string(),
  unpriced_operations: z.string(),
  models: z.array(
    z.object({
      model: z.string(),
      operations: z.string(),
      input_tokens: z.string(),
      cached_input_tokens: z.string(),
      output_tokens: z.string(),
      provider_cost_micro_usd: z.string(),
    })
  ),
});

export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    readonly fields: string[] = []
  ) {
    super(code);
  }
}

export const SIGN_IN_REQUIRED_EVENT = 'assist:sign-in-required';

function notifySignInRequired(error: ApiError, signal?: AbortSignal): void {
  if (
    error.status === 401 &&
    error.code === 'SIGN_IN_REQUIRED' &&
    !signal?.aborted &&
    typeof window !== 'undefined'
  )
    window.dispatchEvent(new Event(SIGN_IN_REQUIRED_EVENT));
}

export async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  options: {
    body?: object;
    csrf?: string;
    signal?: AbortSignal;
    method?: 'GET' | 'POST' | 'DELETE';
  } = {}
): Promise<T> {
  const headers = new Headers();
  if (options.body) headers.set('Content-Type', 'application/json');
  if (options.csrf) headers.set('X-Assist-CSRF', options.csrf);
  const response = await fetch(path, {
    method: options.method ?? (options.body ? 'POST' : 'GET'),
    credentials: 'include',
    headers,
    ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });
  if (response.status === 204) return schema.parse(null);
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new ApiError('SERVICE_RESPONSE_INVALID', response.status);
  }
  if (!response.ok) {
    const error = z
      .object({
        error: z.string().regex(/^[A-Z][A-Z0-9_]{0,100}$/u),
        fields: z.array(z.string().max(200)).max(100).optional(),
      })
      .safeParse(body);
    const failure = new ApiError(
      error.success ? error.data.error : 'REQUEST_FAILED',
      response.status,
      error.success && error.data.error === 'CONFIGURATION_INVALID'
        ? error.data.fields
        : undefined
    );
    notifySignInRequired(failure, options.signal);
    throw failure;
  }
  const parsed = schema.safeParse(body);
  if (!parsed.success)
    throw new ApiError('SERVICE_RESPONSE_INVALID', response.status);
  return parsed.data;
}

export const botPath = (scope: BotScope, suffix: string) =>
  `/v1/bots/${scope.companyId}/${scope.botId}/${suffix}`;
export const savedSchema = z.object({ saved: z.boolean() });
export const idSchema = z.object({ id: z.string() });

export async function downloadCsv(path: string): Promise<Blob> {
  const response = await fetch(path, { credentials: 'include' });
  if (!response.ok) {
    const error = z
      .object({ error: z.string().regex(/^[A-Z][A-Z0-9_]{0,100}$/u) })
      .safeParse(await response.json());
    const failure = new ApiError(
      error.success ? error.data.error : 'REQUEST_FAILED',
      response.status
    );
    notifySignInRequired(failure);
    throw failure;
  }
  if (!response.headers.get('Content-Type')?.startsWith('text/csv'))
    throw new ApiError('SERVICE_RESPONSE_INVALID', response.status);
  return response.blob();
}
